/**
 * Step2_Budget.js
 *
 * BUY / PRESALE / COLAB → single purchase_budget_max field
 *
 * RENT → 4-input model:
 *   ideal_deposit  — comfortable deposit amount
 *   ideal_rent     — comfortable monthly rent
 *   deposit_budget_max (conditional) — max deposit they'd stretch to
 *   rent_budget_max    (conditional) — max rent they'd stretch to
 *
 * Sent to backend:
 *   total_budget       = ideal_deposit + ideal_rent × 33
 *   deposit_budget_max = max deposit (hard ceiling)
 *   rent_budget_max    = max rent    (hard ceiling)
 *
 * The matching engine explores all (deposit, rent) pairs where:
 *   deposit ≤ deposit_budget_max  AND
 *   rent    ≤ rent_budget_max     AND
 *   deposit + rent × 33 ≤ total_budget
 */

import { useState } from 'react';
import { updateDraft } from '../services/propertyRequestService';

const CONVERSION = 33; // 33M deposit ≈ 1M rent/month

const fmt = (v) => (v ? Number(v).toLocaleString('fa-IR') : '');

/** Convert deposit ↔ rent using the 1:33 ratio */
const depositToRent   = (d) => Math.round(d / CONVERSION);
const rentToDeposit   = (r) => r * CONVERSION;

export default function Step2_Budget({ requestId, intent, onNext, onBack, initialData = {} }) {
    const isRent = intent === 'RENT';

    /* ── BUY / PRESALE / COLAB ── */
    const [purchaseMax, setPurchaseMax] = useState(initialData.purchase_budget_max || '');

    /* ── RENT: ideal values ── */
    const [idealDeposit, setIdealDeposit] = useState(
        initialData.ideal_deposit ?? ''
    );
    const [idealRent, setIdealRent] = useState(
        initialData.ideal_rent ?? ''
    );

    /* ── RENT: max (stretch) values ── */
    const [depositMax, setDepositMax] = useState(initialData.deposit_budget_max || '');
    const [rentMax, setRentMax]       = useState(initialData.rent_budget_max || '');

    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState('');

    /* ── derived totals ── */
    const totalBudget = (idealDeposit && idealRent)
        ? Number(idealDeposit) + Number(idealRent) * CONVERSION
        : null;

    /* equivalent rent if user maxes out deposit */
    const rentIfMaxDeposit = (depositMax && totalBudget)
        ? Math.max(0, Math.round((totalBudget - Number(depositMax)) / CONVERSION))
        : null;

    /* equivalent deposit if user maxes out rent */
    const depositIfMaxRent = (rentMax && totalBudget)
        ? Math.max(0, totalBudget - Number(rentMax) * CONVERSION)
        : null;

    /* ── validation ── */
    const validate = () => {
        if (!isRent) return true;
        if (idealDeposit && depositMax && Number(depositMax) < Number(idealDeposit)) {
            setError('سقف ودیعه نمی‌تواند کمتر از ودیعه ایده‌آل باشد.');
            return false;
        }
        if (idealRent && rentMax && Number(rentMax) < Number(idealRent)) {
            setError('سقف اجاره نمی‌تواند کمتر از اجاره ایده‌آل باشد.');
            return false;
        }
        return true;
    };

    const handleNext = async () => {
        if (!validate()) return;
        setError('');
        setSaving(true);
        try {
            const patch = {};
            if (isRent) {
                if (totalBudget)  patch.total_budget       = totalBudget;
                if (depositMax)   patch.deposit_budget_max = Number(depositMax);
                if (rentMax)      patch.rent_budget_max    = Number(rentMax);
            } else {
                if (purchaseMax)  patch.purchase_budget_max = Number(purchaseMax);
            }
            if (Object.keys(patch).length) await updateDraft(requestId, patch);
            // also carry ideal values forward for review screen (not sent to backend)
            onNext({
                ...patch,
                ...(isRent ? { ideal_deposit: Number(idealDeposit) || null, ideal_rent: Number(idealRent) || null } : {}),
            });
        } catch {
            setError('خطا در ذخیره بودجه. لطفاً دوباره تلاش کنید.');
        } finally {
            setSaving(false);
        }
    };

    /* ── render ── */
    return (
        <div>
            <h2 className="prw-step-title">بودجه شما چقدر است؟</h2>
            <p className="prw-step-desc">مقادیر را به تومان وارد کنید. همه فیلدها اختیاری هستند.</p>

            {/* ════ BUY / PRESALE / COLAB ════ */}
            {!isRent && (
                <div className="prw-field">
                    <label className="prw-label" htmlFor="s2-purchase">حداکثر بودجه خرید (تومان)</label>
                    <input
                        id="s2-purchase"
                        type="number"
                        min="0"
                        className="prw-input"
                        placeholder="مثال: ۵۰۰۰۰۰۰۰۰۰"
                        value={purchaseMax}
                        onChange={e => setPurchaseMax(e.target.value)}
                    />
                    {purchaseMax && <span className="s2-hint">{fmt(purchaseMax)} تومان</span>}
                </div>
            )}

            {/* ════ RENT ════ */}
            {isRent && (
                <>
                    {/* ── Section 1: ideal values ── */}
                    <div className="s2-section-label">مقادیر ایده‌آل شما</div>
                    <p className="s2-section-desc">
                        ودیعه و اجاره‌ای که راحت‌ترین حالت برای شماست را وارد کنید.
                    </p>

                    <div className="s3-row">
                        <div className="prw-field">
                            <label className="prw-label" htmlFor="s2-ideal-deposit">ودیعه ایده‌آل (تومان)</label>
                            <input
                                id="s2-ideal-deposit"
                                type="number"
                                min="0"
                                className="prw-input"
                                placeholder="مثال: ۴۰۰٬۰۰۰٬۰۰۰"
                                value={idealDeposit}
                                onChange={e => setIdealDeposit(e.target.value)}
                            />
                            {idealDeposit && <span className="s2-hint">{fmt(idealDeposit)} تومان</span>}
                        </div>
                        <div className="prw-field">
                            <label className="prw-label" htmlFor="s2-ideal-rent">اجاره ایده‌آل (تومان/ماه)</label>
                            <input
                                id="s2-ideal-rent"
                                type="number"
                                min="0"
                                className="prw-input"
                                placeholder="مثال: ۹٬۰۰۰٬۰۰۰"
                                value={idealRent}
                                onChange={e => setIdealRent(e.target.value)}
                            />
                            {idealRent && <span className="s2-hint">{fmt(idealRent)} تومان</span>}
                        </div>
                    </div>

                    {/* ── Total budget pill ── */}
                    {totalBudget && (
                        <div className="s2-total-box">
                            <span>بودجه کل معادل:</span>
                            <strong>{fmt(totalBudget)} تومان</strong>
                            <span className="s2-total-note">
                                (هر {fmt(CONVERSION * 1_000_000)} تومان ودیعه = ۱٬۰۰۰٬۰۰۰ تومان اجاره)
                            </span>
                        </div>
                    )}

                    {/* ── Section 2: stretch limits ── */}
                    <div className="s2-section-label s2-section-label--mt">سقف قابل انعطاف</div>
                    <p className="s2-section-desc">
                        اگر حاضرید ودیعه را افزایش دهید و اجاره را کاهش دهید (یا برعکس)، حداکثر هر کدام چقدر است؟
                    </p>

                    <div className="s3-row">
                        <div className="prw-field">
                            <label className="prw-label" htmlFor="s2-deposit-max">حداکثر ودیعه (تومان)</label>
                            <input
                                id="s2-deposit-max"
                                type="number"
                                min="0"
                                className="prw-input"
                                placeholder="مثال: ۷۰۰٬۰۰۰٬۰۰۰"
                                value={depositMax}
                                onChange={e => setDepositMax(e.target.value)}
                            />
                            {depositMax && <span className="s2-hint">{fmt(depositMax)} تومان</span>}
                            {rentIfMaxDeposit !== null && (
                                <span className="s2-equiv">
                                    ← اجاره معادل: {fmt(rentIfMaxDeposit)} تومان/ماه
                                </span>
                            )}
                        </div>
                        <div className="prw-field">
                            <label className="prw-label" htmlFor="s2-rent-max">حداکثر اجاره (تومان/ماه)</label>
                            <input
                                id="s2-rent-max"
                                type="number"
                                min="0"
                                className="prw-input"
                                placeholder="مثال: ۲۰٬۰۰۰٬۰۰۰"
                                value={rentMax}
                                onChange={e => setRentMax(e.target.value)}
                            />
                            {rentMax && <span className="s2-hint">{fmt(rentMax)} تومان</span>}
                            {depositIfMaxRent !== null && (
                                <span className="s2-equiv">
                                    ← ودیعه معادل: {fmt(depositIfMaxRent)} تومان
                                </span>
                            )}
                        </div>
                    </div>
                </>
            )}

            {error && <p className="prw-error">{error}</p>}

            <div className="prw-nav">
                <button className="prw-btn prw-btn-secondary" onClick={onBack}>→ مرحله قبل</button>
                <button className="prw-btn prw-btn-primary" onClick={handleNext} disabled={saving}>
                    {saving ? 'در حال ذخیره...' : 'مرحله بعد ←'}
                </button>
            </div>
        </div>
    );
}
